/**
 * RTK Query API for authentication endpoints
 *
 * Thin API layer — only defines endpoint queries/mutations.
 * All auth orchestration (token storage, logout flow, redirects)
 * is handled by authController, which dispatches these endpoints.
 * Token refresh is handled by axiosInstance interceptors.
 *
 * Contract: endpoints validate against @gearghost/api-schemas/auth.
 * Login/me return the unwrapped payload; signup and the message-only
 * mutations return the WHOLE envelope (consumers read `.message` /
 * `.status`).
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';
import {
  AUTH_ROUTES,
  SUDO_ROUTES,
  CookieLoginEnvelopeSchema,
  CookieLoginResultSchema,
  unwrapCookieLogin,
  MeEnvelopeSchema,
  MeSchema,
  unwrapMe,
  SignupEnvelopeSchema,
  MessageOnlyEnvelopeSchema,
  VerifyEmailEnvelopeSchema,
  VerifyResetTokenEnvelopeSchema,
} from '@gearghost/api-schemas/auth';
import type {
  CookieLoginResult,
  Me,
  MessageOnlyEnvelope as MessageOnly,
  SigninRequest,
  SignupRequest,
  ResendVerificationRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UserSudoRequest,
} from '@gearghost/api-schemas/auth';
import axiosBaseQuery from './axiosBaseQuery';
import { onSchemaFailure } from './onSchemaFailure';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  endpoints: (builder) => ({
    /**
     * Login endpoint — returns the unwrapped login payload (a union:
     * success carries the user + access token, the refresh token going
     * into the HttpOnly cookie (#556 "Migrate web refresh token to
     * HttpOnly cookie"); unverified accounts get
     * {id, requireVerification: true}). Strict: a refresh token back in
     * the body fails the parse. Token storage and navigation are handled
     * by authController.login().
     */
    login: builder.mutation<CookieLoginResult, SigninRequest>({
      query: (credentials) => ({
        url: AUTH_ROUTES.signin,
        method: 'POST',
        body: credentials,
      }),
      rawResponseSchema: CookieLoginEnvelopeSchema,
      transformResponse: unwrapCookieLogin,
      responseSchema: CookieLoginResultSchema,
    }),

    /**
     * Logout endpoint — notifies the server of logout.
     * State cleanup and redirect are handled by authController.logout().
     * no responseSchema — UI reads only success/failure; body is ignored.
     */
    logout: builder.mutation<void, void>({
      query: () => ({
        url: AUTH_ROUTES.logout,
        method: 'GET',
      }),
    }),

    /**
     * Get current user info and token validity.
     * Used to verify token is still valid.
     */
    me: builder.query<Me, void>({
      query: () => AUTH_ROUTES.me,
      rawResponseSchema: MeEnvelopeSchema,
      transformResponse: unwrapMe,
      responseSchema: MeSchema,
    }),

    /**
     * Sign up a new user. Returns the strict {id,firstName,lastName,email}
     * projection (SignupResponseSchema — the payload once leaked the bcrypt
     * hash) inside the envelope; consumers read `.message`.
     */
    signup: builder.mutation<z.infer<typeof SignupEnvelopeSchema>, SignupRequest>({
      query: (userData) => ({
        url: AUTH_ROUTES.signup,
        method: 'POST',
        body: userData,
      }),
      rawResponseSchema: SignupEnvelopeSchema,
      responseSchema: SignupEnvelopeSchema,
    }),

    /**
     * Verify email from email link token.
     * Modeled as a mutation because it's a one-shot server-side state change
     * that should not be auto-cached or auto-refetched.
     * A replayed link is a 200 with `data.alreadyVerified: true` — the
     * screen renders it as success, not an error.
     */
    verifyEmail: builder.mutation<
      z.infer<typeof VerifyEmailEnvelopeSchema>,
      string
    >({
      query: (token) => ({
        url: AUTH_ROUTES.verifyEmail,
        method: 'POST',
        body: { token },
      }),
      rawResponseSchema: VerifyEmailEnvelopeSchema,
      responseSchema: VerifyEmailEnvelopeSchema,
    }),

    /**
     * Completes a pending email CHANGE. Distinct endpoint from
     * verifyEmail because the token types are distinct: this link was
     * mailed to the CANDIDATE address and its click swaps the login
     * identifier, so feeding it to the signup verifier is a type
     * mismatch that fails closed as "Invalid link".
     */
    confirmEmailChange: builder.mutation<MessageOnly, string>({
      query: (token) => ({
        url: AUTH_ROUTES.confirmEmailChange,
        method: 'POST',
        body: { token },
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
    }),

    /**
     * Re-send the verification email — from the "check your email" screen
     * after signup, or after an unverified login attempt. Mobile has had
     * this flow since launch; added to web as parity divergence #10.
     */
    resendVerification: builder.mutation<MessageOnly, ResendVerificationRequest>({
      query: (body) => ({
        url: AUTH_ROUTES.resendVerification,
        method: 'POST',
        body,
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
    }),

    /**
     * Verify that a password reset token is still valid.
     * Modeled as a mutation (one-shot check, no caching).
     *
     * POST with body (not GET with query) so the token doesn't leak
     * into URLs, browser history, Referer headers, or nginx logs.
     */
    verifyResetToken: builder.mutation<
      z.infer<typeof VerifyResetTokenEnvelopeSchema>,
      string
    >({
      query: (token) => ({
        url: AUTH_ROUTES.verifyResetToken,
        method: 'POST',
        body: { token },
      }),
      rawResponseSchema: VerifyResetTokenEnvelopeSchema,
      responseSchema: VerifyResetTokenEnvelopeSchema,
    }),

    /**
     * Reset password using a token from the reset-password email.
     */
    resetPasswordWithToken: builder.mutation<MessageOnly, ResetPasswordRequest>({
      query: (body) => ({
        url: AUTH_ROUTES.resetPasswordWithToken,
        method: 'POST',
        body,
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
    }),

    // Request a password reset email
    forgotPassword: builder.mutation<MessageOnly, ForgotPasswordRequest>({
      query: (body) => ({
        url: AUTH_ROUTES.forgotPassword,
        method: 'PUT',
        body,
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
    }),

    // The signed-in user's password again, to open the sudo window before an
    // action that answered 403 with X-Sudo-Required. Asked only by the one
    // password prompt (SudoPasswordPrompt).
    enterSudo: builder.mutation<MessageOnly, UserSudoRequest>({
      query: (body) => ({
        url: SUDO_ROUTES.user,
        method: 'POST',
        body,
      }),
      rawResponseSchema: MessageOnlyEnvelopeSchema,
      responseSchema: MessageOnlyEnvelopeSchema,
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useMeQuery,
  useSignupMutation,
  useVerifyEmailMutation,
  useConfirmEmailChangeMutation,
  useResendVerificationMutation,
  useVerifyResetTokenMutation,
  useResetPasswordWithTokenMutation,
  useForgotPasswordMutation,
  useEnterSudoMutation,
} = authApi;
